import React from 'react'
import { BarCanvasCustomLayer, BarDatum, ResponsiveBarCanvas } from '@nivo/bar'
import { line } from 'd3-shape'
import { Box } from '@material-ui/core'
import { BarTooltip } from '../../../common/charts/BarTooltip'
import {
  COLOR_SCHEME_SET1,
  COLOR_SCHEME_SET1_PLUS1,
  CustomLineStyles,
} from '../chart-util'

type Props = {
  data: BarDatum[]
  keys: string[]
  measuredSerieId: string | undefined
  xFormatter: (id: string) => string
  yFormatter: (value: number) => string
  maxValue: number
  axisBottomLegend?: string
  axisLeftLegend?: string
  legendItemWidth?: number
  grouped?: boolean
  colors?: string[]
}

let customLineSerieId: string | undefined

const CustomLines: BarCanvasCustomLayer<BarDatum> = (ctx, props) => {
  const { bars } = props
  const points: [number, number][] = []

  bars.forEach(({ data, x, y, width }) => {
    if (data.id === customLineSerieId)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      points.push([x + width / 2, y])
  })

  const lineGenerator = line()
    .x((d) => d[0])
    .y((d) => d[1])

  const path = lineGenerator(points)

  if (path) {
    const path2d = new Path2D(path)
    ctx.save()
    const [color] = COLOR_SCHEME_SET1_PLUS1
    ctx.strokeStyle = color
    ctx.lineWidth = 4
    ctx.setLineDash(CustomLineStyles.Dotted.strokeDashArray ?? [])

    ctx.stroke(path2d)
    ctx.restore()
  }
}

export function CalibrationBarChart({
  data,
  keys,
  measuredSerieId,
  xFormatter,
  yFormatter,
  maxValue,
  axisBottomLegend,
  axisLeftLegend,
  legendItemWidth,
  grouped,
  colors,
}: Props): React.ReactElement {
  customLineSerieId = measuredSerieId

  const colorSet = colors
    ? colors.slice(0, keys.length)
    : (measuredSerieId ? COLOR_SCHEME_SET1 : COLOR_SCHEME_SET1_PLUS1).slice(
        0,
        keys.length,
      )

  return (
    <Box position="relative" height="100%" width="100%">
      <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={0}>
        <ResponsiveBarCanvas
          data={data}
          keys={keys.filter((k) => k !== customLineSerieId)}
          groupMode={grouped ? 'grouped' : 'stacked'}
          isInteractive
          enableLabel={false}
          indexBy="id"
          valueScale={{
            type: 'linear',
            max: maxValue,
            min: measuredSerieId ? undefined : 'auto',
          }}
          colors={colorSet}
          margin={{ top: 8, right: 10, bottom: 90, left: 50 }}
          padding={0.7}
          tooltip={({ id, value }) =>
            BarTooltip(xFormatter(String(id)), yFormatter(value))
          }
          axisBottom={{
            legend: axisBottomLegend,
            legendOffset: 35,
          }}
          axisLeft={{
            // format: yFormatter,
            legend: axisLeftLegend,
            legendOffset: -35,
          }}
          layers={['grid', 'bars', 'axes']}
          legends={
            !measuredSerieId
              ? [
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom',
                    direction: 'row',
                    itemHeight: 10,
                    itemWidth: legendItemWidth ?? 100,
                    itemsSpacing: 20,
                    translateY: 60,
                    symbolSize: 20,
                  },
                ]
              : undefined
          }
        />
      </Box>
      {measuredSerieId && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
        >
          <ResponsiveBarCanvas
            data={data}
            keys={keys}
            groupMode={grouped ? 'grouped' : 'stacked'}
            isInteractive
            enableLabel={false}
            indexBy="id"
            valueScale={{ type: 'linear', max: maxValue }}
            colors={COLOR_SCHEME_SET1_PLUS1.slice(0, keys.length)}
            margin={{ top: 8, right: 10, bottom: 90, left: 50 }}
            padding={0.7}
            tooltip={({ id, value }) =>
              BarTooltip(xFormatter(String(id)), yFormatter(value))
            }
            layers={[CustomLines, 'legends']}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                itemHeight: 10,
                itemWidth: legendItemWidth ?? 100,
                itemsSpacing: 20,
                translateY: 65,
                symbolSize: 20,
              },
            ]}
          />
        </Box>
      )}
    </Box>
  )
}
